import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useReportUi, breakdownSemantic, reportCardShell } from './useReportUi';
import ReportPageHeader from './ReportPageHeader';
import ReportMetricCard from './ReportMetricCard';
import ReportBreakdownCard from './ReportBreakdownCard';
import ChartCard from './ChartCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Button } from '../../../components/ui';
import {
  buildYearOptions,
  countBy,
  countByLabels,
  countByMonth,
  currentYear,
  filterRecordsByYear,
  formatReportDate,
  getRecordDateValue,
  MONTH_LABELS,
  recordTitle,
} from './reportYearUtils';

function displayLabel(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'object') {
    return value.name || value.label || value.title || '—';
  }
  return String(value);
}

function mapBreakdown(entries, limit = 12) {
  return Object.entries(entries)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([label, count]) => ({
      label: displayLabel(label),
      count,
      semantic: breakdownSemantic(displayLabel(label)),
    }));
}

function looksClosed(record, statusLabel) {
  if (record?.completed === true) return true;
  if (record?.closedAt) return true;
  const s = String(statusLabel || '').toLowerCase();
  return (
    s.includes('closed') ||
    s.includes('resolved') ||
    s.includes('complete') ||
    s.includes('cancelled') ||
    s.includes('done')
  );
}

/**
 * Shared year-scoped analytics page for Reports & Analytics modules.
 */
const YearModuleAnalyticsReport = ({
  title,
  description,
  useData,
  dateField = 'createdAt',
  getStatusLabel = (row) =>
    row?.status?.name ||
    row?.status ||
    (row?.completed === true ? 'Completed' : row?.completed === false ? 'Open' : '—'),
  getSecondaryLabel,
  secondaryTitle = 'Breakdown',
  /** Extra breakdown cards: [{ title, getLabel?, getLabels?, description? }] */
  extraBreakdowns = [],
  getRowLabel = recordTitle,
  fromYear = 2020,
}) => {
  const { theme, dash, cn } = useReportUi();
  const shell = reportCardShell(dash, cn);
  const [searchParams, setSearchParams] = useSearchParams();
  const yearOptions = useMemo(() => buildYearOptions(fromYear), [fromYear]);

  const resolveYear = (raw) => {
    const y = Number(raw);
    if (Number.isFinite(y) && yearOptions.includes(y)) return y;
    return currentYear();
  };

  const [year, setYear] = useState(() => resolveYear(searchParams.get('year')));
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const { data = [], isLoading, isError, mutate } = useData();

  /** Always keep a valid year in state; default missing/invalid URL to current year. */
  useEffect(() => {
    const next = resolveYear(searchParams.get('year'));
    setYear(next);
    const raw = searchParams.get('year');
    if (raw !== String(next)) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set('year', String(next));
          return params;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resolveYear closes over yearOptions
  }, [searchParams, yearOptions, setSearchParams]);

  const yearRecords = useMemo(
    () => filterRecordsByYear(data, year, dateField),
    [data, year, dateField],
  );

  const analytics = useMemo(() => {
    const total = yearRecords.length;
    const byStatus = countBy(yearRecords, (r) => displayLabel(getStatusLabel(r)));
    const closed = yearRecords.filter((r) =>
      looksClosed(r, displayLabel(getStatusLabel(r))),
    ).length;
    const open = total - closed;
    const months = countByMonth(yearRecords, dateField);
    /** Full Jan–Dec series for bar chart (zeros keep the axis readable). */
    const monthChartData = MONTH_LABELS.map((label, i) => ({
      label,
      value: months[i],
      semantic: 'info',
    }));
    const byMonthItems = MONTH_LABELS.map((label, i) => ({
      label,
      count: months[i],
      semantic: 'info',
    })).filter((m) => m.count > 0);
    const statusChartData = Object.entries(byStatus)
      .map(([label, value]) => ({
        label: displayLabel(label),
        value,
        semantic: breakdownSemantic(displayLabel(label)),
      }))
      .sort((a, b) => b.value - a.value);
    const bySecondary = getSecondaryLabel
      ? countBy(yearRecords, (r) => displayLabel(getSecondaryLabel(r)))
      : null;
    const extraCounts = (Array.isArray(extraBreakdowns) ? extraBreakdowns : [])
      .filter(
        (b) =>
          b &&
          (typeof b.getLabels === 'function' || typeof b.getLabel === 'function'),
      )
      .map((b) => ({
        title: b.title || 'Breakdown',
        description: b.description,
        counts:
          typeof b.getLabels === 'function'
            ? countByLabels(yearRecords, (r) =>
                (b.getLabels(r) || []).map((v) => displayLabel(v)),
              )
            : countBy(yearRecords, (r) => displayLabel(b.getLabel(r))),
      }));
    const recent = [...yearRecords]
      .sort((a, b) => {
        const da = getRecordDateValue(a, dateField)?.getTime() || 0;
        const db = getRecordDateValue(b, dateField)?.getTime() || 0;
        return db - da;
      })
      .slice(0, 25);
    return {
      total,
      open,
      closed,
      openRate: total > 0 ? Math.round((open / total) * 100) : 0,
      byStatus,
      monthChartData,
      byMonthItems,
      statusChartData,
      bySecondary,
      extraCounts,
      recent,
    };
  }, [yearRecords, dateField, getStatusLabel, getSecondaryLabel, extraBreakdowns]);

  const statusItems = useMemo(
    () => mapBreakdown(analytics.byStatus),
    [analytics.byStatus],
  );
  const secondaryItems = useMemo(
    () => (analytics.bySecondary ? mapBreakdown(analytics.bySecondary) : []),
    [analytics.bySecondary],
  );
  const extraBreakdownItems = useMemo(
    () =>
      (analytics.extraCounts || []).map((b) => ({
        title: b.title,
        description: b.description,
        items: mapBreakdown(b.counts),
      })),
    [analytics.extraCounts],
  );

  const handleYearChange = (next) => {
    const y = Number(next);
    if (!Number.isFinite(y) || !yearOptions.includes(y)) return;
    setYear(y);
    setLastUpdated(new Date());
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('year', String(y));
        return params;
      },
      { replace: true },
    );
  };

  if (isLoading) {
    return (
      <div className={cn('min-h-screen p-6 flex items-center justify-center', dash.pageBg())}>
        <div
          className={cn(
            'animate-spin rounded-full h-10 w-10 border-2 border-t-transparent',
            dash.fill('primary'),
          )}
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cn('min-h-screen p-6', dash.pageBg())}>
        <Card className={shell}>
          <CardContent className="pt-6">
            <p className={dash.text('danger')}>Could not load report data.</p>
            <Button type="button" variant="outline" className="mt-4" onClick={() => mutate()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn('min-h-full p-4 md:p-6', dash.pageBg())}>
      <div className="max-w-7xl mx-auto space-y-6">
        <ReportPageHeader
          title={title}
          description={description}
          lastUpdated={lastUpdated}
          year={year}
          yearOptions={yearOptions}
          onYearChange={handleYearChange}
          onRefresh={() => {
            setLastUpdated(new Date());
            mutate();
          }}
          theme={theme}
          dash={dash}
          cn={cn}
          cardClassName={shell}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <ReportMetricCard label={`Total (${year})`} value={analytics.total} semantic="info" />
          <ReportMetricCard label="Open / active" value={analytics.open} semantic="warning" />
          <ReportMetricCard label="Closed / done" value={analytics.closed} semantic="success" />
          <ReportMetricCard
            label="Open rate"
            value={`${analytics.openRate}%`}
            semantic={analytics.openRate > 40 ? 'warning' : 'success'}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard
            title={`Monthly volume (${year})`}
            description="Records by month in the selected year."
            type="bar"
            data={analytics.monthChartData}
          />
          <ChartCard
            title={`Status mix (${year})`}
            description="Share of records by status."
            type="doughnut"
            data={analytics.statusChartData}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ReportBreakdownCard
            title="By status"
            description={`Status distribution for ${year}.`}
            items={statusItems}
            total={analytics.total}
            theme={theme}
            dash={dash}
            cn={cn}
            cardClassName={shell}
          />
          {getSecondaryLabel ? (
            <ReportBreakdownCard
              title={secondaryTitle}
              description={`Secondary breakdown for ${year}.`}
              items={secondaryItems}
              total={analytics.total}
              theme={theme}
              dash={dash}
              cn={cn}
              cardClassName={shell}
            />
          ) : (
            <ReportBreakdownCard
              title="By month"
              description={`Monthly counts for ${year}.`}
              items={analytics.byMonthItems}
              total={analytics.total}
              theme={theme}
              dash={dash}
              cn={cn}
              cardClassName={shell}
              emptyMessage="No records in this year."
            />
          )}
          {extraBreakdownItems.map((b) => (
            <ReportBreakdownCard
              key={b.title}
              title={b.title}
              description={b.description || `Distribution for ${year}.`}
              items={b.items}
              total={analytics.total}
              theme={theme}
              dash={dash}
              cn={cn}
              cardClassName={shell}
            />
          ))}
        </div>

        <Card className={shell}>
          <CardHeader>
            <CardTitle className="text-lg">Recent records ({year})</CardTitle>
            <CardDescription>Newest 25 records in the selected year.</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics.recent.length === 0 ? (
              <p className={cn('text-sm', theme.text.muted)}>No records for {year}.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead>
                    <tr
                      className={cn(
                        'border-b text-left bg-black/[0.03] dark:bg-white/[0.03]',
                        theme.border.light,
                      )}
                    >
                      <th className="py-2 pr-3 font-medium">ID</th>
                      <th className="py-2 pr-3 font-medium">Title</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.recent.map((row, index) => (
                      <tr
                        key={row.id ?? `${getRowLabel(row)}-${index}`}
                        className={cn(
                          'border-b',
                          index % 2 === 1 ? 'bg-black/[0.015] dark:bg-white/[0.015]' : '',
                          theme.border.light,
                        )}
                      >
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {row.id != null ? `#${row.id}` : '—'}
                        </td>
                        <td className="py-2 pr-3 max-w-[280px] truncate">
                          {displayLabel(getRowLabel(row))}
                        </td>
                        <td className="py-2 pr-3">{displayLabel(getStatusLabel(row))}</td>
                        <td className="py-2 tabular-nums whitespace-nowrap">
                          {formatReportDate(getRecordDateValue(row, dateField))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default YearModuleAnalyticsReport;
